import 'knockout.validation';
import * as ko from 'knockout';

import { ISelfResponse } from '@/shared/interfaces';
import { Log } from '@/shared/Log';
import { R } from '@/Resources.resx';
import { Utils } from '@/shared/Utils';
import { AlertViewModel } from './AlertViewModel';
import Modal from '@/shared/Modal';
import { fetch } from '@/shared/fetch';

export class ProfileViewModel {
    public profileState: KnockoutObservable<string>;
    public isSubmitting: KnockoutComputed<boolean>;

    // My information
    public email: KnockoutObservable<string>;

    public newsletterSwitch = ko.observable(false);
    public newsletterSwitchDisabled = ko.observable(false);

    private profileStates = {
        ready: 'ready',
        saving: 'saving',
        saved: 'saved',
    };

    private alertModal: AlertViewModel;

    constructor() {
        const self = this;
        self.alertModal = new AlertViewModel();

        self.profileState = ko.observable(self.profileStates.ready);
        self.isSubmitting = ko.computed(() => {
            return self.profileState() == self.profileStates.saving;
        });

        self.email = ko.observable('').extend({
            required: {
                param: true,
                message: R.EmailRequired,
            },
            maxLength: 256,
            email: {
                message: R.InvalidEmail,
            },
        });
    }

    public getProfileData = async (onlyPartnerData = false) => {
        const self = this;

        await fetch('/api/patients/self', 'GET')
            .then((res: ISelfResponse) => {
                self.getProfileDataCallback(res, onlyPartnerData);
            })
            .catch((err) => {
                self.alertModal.openAlert(
                    R.ErrorFetchingProfileData + '\n' + err,
                );
            });
    };

    public getProfileDataCallback(
        response: ISelfResponse,
        onlyPartnerData: boolean,
    ) {
        const self = this;
        if (!onlyPartnerData) {
            if (!response.Me || !response.Me.User) {
                Log.error('getProfileDataCallback: User not set', response);
                self.alertModal.openAlert(R.ErrorFetchingProfileData);
                return;
            }
            self.email(response.Me.Email);
            self.newsletterSwitch(response.Me.AcceptMarketing);
            self.newsletterSwitch.subscribe((newValue) =>
                this.toggleNewsletterSwitch(newValue),
            );
        }
    }

    public deleteAccount(): void {
        fetch('/api/patients/account', 'DELETE')
            .then(() => {
                this.closeDeleteAccountModal();
                Utils.logout();
            })
            .catch((err) => {
                this.closeDeleteAccountModal();
                this.alertModal.openAlert(R.ErrorDeleteAccount);
            });
    }

    public openDeleteAccountModal(): void {
        Modal.show('delete-account-modal');
    }

    public closeDeleteAccountModal(): void {
        Modal.close('delete-account-modal');
    }

    public closeConfirmModal(): void {
        Modal.close('confirm-modal');
    }

    public openRemoveInsurancePartnerModal(): void {
        Modal.show('remove-insurance-partner-modal');
    }

    public closeRemoveInsurancePartnerModal(): void {
        Modal.close('remove-insurance-partner-modal');
    }

    public openUnsubscribeModal(): void {
        Modal.show('confirm-unsubscribe-modal');
    }

    public closeUnsubscribeModal(): void {
        Modal.close('confirm-unsubscribe-modal');
    }

    public async toggleNewsletterSwitch(newValue: boolean) {
        const self = this;
        self.newsletterSwitchDisabled(true);

        const url = newValue
            ? `/email/subscribe?email=${encodeURIComponent(self.email())}&getView=false`
            : `/email/unsubscribe?email=${encodeURIComponent(self.email())}&getView=false`;

        await fetch(url, 'GET')
            .then((_: ISelfResponse) => {
                self.newsletterSwitchDisabled(false);
                self.newsletterSwitch(newValue);
            })
            .catch((err) => {
                self.newsletterSwitchDisabled(false);
                self.alertModal.openAlert(
                    R.ErrorTogglingNewsletter + '\n' + err,
                );
            });
    }

    public chooseLanguageOnEnter(
        data: ProfileViewModel,
        event: JQuery.Event,
        languageCode: 'da' | 'en' | 'no',
    ) {
        if (event.key !== 'Enter') return true; // to avoid prevention of default behaviour

        window.location.href =
            window.location.pathname + '?lang=' + languageCode;
    }

    public toggleNewsletterOnEnter(
        data: ProfileViewModel,
        event: JQuery.Event,
    ) {
        if (event.key !== 'Enter') return true; // to avoid prevention of default behaviour

        this.toggleNewsletterSwitch(!this.newsletterSwitch()).then(null);
    }
}
